import { useCallback, useEffect, useRef, useState } from "react";
import { X, Zap, ZapOff, Check, Camera, Trash2, Flashlight, FlashlightOff, Images, ChevronLeft, ChevronRight, Smartphone, AlertTriangle } from "lucide-react";
import { analyzeFrame, createWorkspace, quadMotion, quadCentroid, warpQuad, enhance, canvasToBlob, measureSharpness, canvasSharpness, detectOnCanvas } from "./docDetect";
import { createDemoStream } from "./demoStream";
import { getCameraStream, releaseCameraStream, stopCameraStream, waitForFrames } from "./cameraStream";
import { optimisePhotoCanvas } from "./photoPipeline";

function thumbnail(canvas) {
  const tc = document.createElement("canvas");
  const sc = 240 / Math.max(canvas.width, canvas.height);
  tc.width = canvas.width * sc; tc.height = canvas.height * sc;
  tc.getContext("2d").drawImage(canvas, 0, 0, tc.width, tc.height);
  return tc.toDataURL("image/jpeg", 0.7);
}

const IOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

const STEADY_FRAMES = 5, MOTION_LIMIT = 0.012, SHARP_MIN = 5, FOCUS_MIN = 9, REMOVED_FRAMES = 8, ANALYSIS_MS = 100, MAX_WAIT_FRAMES = 12;
const MODES = [["single", "SINGLE", "One invoice — captures then finishes"], ["multiple", "MULTIPLE", "Many invoices, one page each"], ["combine", "COMBINE", "Several pages of ONE invoice"]];
const STATUS = {
  searching: ["Looking for invoice…", "bg-black/60 text-white"], detected: ["Invoice detected", "bg-amber-500 text-slate-950"],
  steady: ["Hold steady…", "bg-emerald-500 text-slate-950"], captured: ["Captured!", "bg-emerald-400 text-slate-950"],
  cooldown: ["Remove invoice to scan the next one", "bg-black/60 text-emerald-200"], manual: ["Tap the shutter to capture", "bg-black/60 text-white"],
  blurry: ["Too blurry — hold still or add light", "bg-rose-500 text-white"], partial: ["Fit the whole invoice in view", "bg-amber-500 text-slate-950"],
};

async function grabFrame(video, track) {
  const full = document.createElement("canvas");
  if (track && window.ImageCapture) {
    try {
      // takePhoto can stall for seconds on some phones (refocus + shutter): give it 1.2s, otherwise use the live frame
      const blob = await Promise.race([new ImageCapture(track).takePhoto(), new Promise((_, rej) => setTimeout(() => rej(new Error("slow")), 1200))]);
      const bmp = await createImageBitmap(blob);
      if (Math.abs(bmp.width / bmp.height - video.videoWidth / video.videoHeight) < 0.02) {
        full.width = bmp.width; full.height = bmp.height;
        full.getContext("2d").drawImage(bmp, 0, 0);
        return full;
      }
    } catch { /* fall back to video frame */ }
  }
  // Burst: grab 3 frames over ~250ms and keep the sharpest one.
  let best = null, bestScore = -1;
  for (let i = 0; i < 3; i++) {
    const c = document.createElement("canvas");
    c.width = video.videoWidth; c.height = video.videoHeight;
    c.getContext("2d").drawImage(video, 0, 0);
    const score = canvasSharpness(c);
    if (score > bestScore) { best = c; bestScore = score; }
    if (i < 2) await new Promise((r) => setTimeout(r, 120));
  }
  return best || full;
}

function Gallery({ invoices, onDelete, onClose }) {
  const pages = invoices.flatMap((inv, ii) => inv.pages.map((p, pi) => ({ ...p, ii, pi })));
  const [idx, setIdx] = useState(Math.max(0, pages.length - 1));
  const touch = useRef(null);
  const cur = pages[Math.min(idx, pages.length - 1)];
  useEffect(() => { if (pages.length === 0) onClose(); }, [pages.length, onClose]);
  const go = (d) => setIdx((i) => Math.min(pages.length - 1, Math.max(0, i + d)));
  const onTouchStart = (e) => { touch.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touch.current === null) return;
    const dx = e.changedTouches[0].clientX - touch.current; touch.current = null;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
  };
  if (!cur) return null;
  return (
    <div className="absolute inset-0 z-40 bg-black flex flex-col" data-testid="capture-gallery">
      <div className="flex items-center justify-between px-4 py-3" style={{ paddingTop: "max(env(safe-area-inset-top), 12px)" }}>
        <p className="font-semibold text-sm" data-testid="gallery-position">{idx + 1} / {pages.length} <span className="text-slate-400 font-normal">· Invoice {cur.ii + 1}{invoices[cur.ii].pages.length > 1 ? `, page ${cur.pi + 1}` : ""}{cur.cropped ? " · auto-cropped" : ""}</span></p>
        <button onClick={onClose} className="h-11 w-11 grid place-items-center rounded-full bg-white/10" data-testid="gallery-close-button"><X /></button>
      </div>
      <div className="relative flex-1 min-h-0 flex items-center justify-center overflow-hidden" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-testid="gallery-viewer">
        <img key={idx} src={cur.full || cur.thumb} alt="" className="max-w-full max-h-full object-contain fade-up" data-testid="gallery-image" />
        {idx > 0 && <button onClick={() => go(-1)} className="absolute left-2 top-1/2 -translate-y-1/2 h-12 w-12 grid place-items-center rounded-full bg-black/50" data-testid="gallery-prev" aria-label="Previous"><ChevronLeft /></button>}
        {idx < pages.length - 1 && <button onClick={() => go(1)} className="absolute right-2 top-1/2 -translate-y-1/2 h-12 w-12 grid place-items-center rounded-full bg-black/50" data-testid="gallery-next" aria-label="Next"><ChevronRight /></button>}
      </div>
      <div className="px-4 py-3 space-y-3" style={{ paddingBottom: "max(env(safe-area-inset-bottom), 12px)" }}>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {pages.map((p, i) => <button key={i} onClick={() => setIdx(i)} className={`h-16 w-12 shrink-0 rounded overflow-hidden border-2 ${i === idx ? "border-amber-400" : "border-white/20"}`} data-testid={`gallery-thumb-${i}`}><img src={p.thumb} alt="" className="w-full h-full object-cover" /></button>)}
        </div>
        <button onClick={() => { onDelete(cur.ii, cur.pi); setIdx((i) => Math.max(0, Math.min(i, pages.length - 2))); }} className="w-full h-12 rounded-full bg-rose-600 font-semibold flex items-center justify-center gap-2" data-testid="gallery-delete-button"><Trash2 className="h-5 w-5" /> Delete this page</button>
      </div>
    </div>
  );
}

export default function Scanner({ initialAutoCapture = true, onAutoCaptureChange, onFinish, onClose, optimise = false, optimiseOpts = null }) {
  const videoRef = useRef(null), boxRef = useRef(null), wsRef = useRef(null), focusRef = useRef(null), trackRef = useRef(null);
  const optimiseRef = useRef(optimise); optimiseRef.current = optimise;
  const optimiseOptsRef = useRef(optimiseOpts); optimiseOptsRef.current = optimiseOpts || {};
  const st = useRef({ prevQuad: null, steady: 0, noDoc: 0, armed: true, lastQuad: null, busy: false, focus: [] }).current;
  const [auto, setAuto] = useState(initialAutoCapture);
  const [mode, setMode] = useState(localStorage.getItem("supplivo_scan_mode") || "multiple");
  const [status, setStatus] = useState("searching");
  const [quad, setQuad] = useState(null);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [flash, setFlash] = useState(false);
  const [torch, setTorch] = useState(null); // null = unsupported
  const [gallery, setGallery] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [upProgress, setUpProgress] = useState(null);
  // leaving mid-upload loses the photos still in the browser → warn on tab close / swipe away
  useEffect(() => {
    if (!uploading) return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [uploading]);
  const [dims, setDims] = useState([3, 4]);
  const [landscape, setLandscape] = useState(false);
  const landscapeRef = useRef(false);
  // scanning is portrait-only: lock where the browser allows it (Android / installed app), otherwise ask to turn the phone
  useEffect(() => {
    const check = () => { const v = window.matchMedia("(orientation: landscape)").matches && window.innerWidth > window.innerHeight && "ontouchstart" in window; landscapeRef.current = v; setLandscape(v); };
    check();
    window.addEventListener("resize", check);
    window.screen.orientation?.lock?.("portrait").catch(() => {});
    return () => { window.removeEventListener("resize", check); try { window.screen.orientation?.unlock?.(); } catch { /* ignore */ } };
  }, []);
  // the camera frame flips between portrait/landscape when the phone rotates (no loadedmetadata is fired) → keep the preview box in sync
  const syncDims = useCallback(() => {
    const v = videoRef.current;
    if (!v?.videoWidth || !v?.videoHeight) return;
    setDims((d) => (d[0] === v.videoWidth && d[1] === v.videoHeight ? d : [v.videoWidth, v.videoHeight]));
  }, []);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const autoRef = useRef(auto); autoRef.current = auto;
  const modeRef = useRef(mode); modeRef.current = mode;
  const finishRef = useRef(null);

  useEffect(() => {
    let cancelled = false, demo = false, demoStream = null;
    (async () => {
      try {
        demo = window.location.search.includes("demo=1");
        let s = demo ? createDemoStream() : await getCameraStream();
        if (demo) demoStream = s;
        if (cancelled) { if (demo) s.getTracks().forEach((t) => t.stop()); else releaseCameraStream(); return; }
        const attach = (ms) => { if (videoRef.current) { videoRef.current.srcObject = ms; videoRef.current.play().catch(() => {}); } };
        attach(s);
        if (!demo && videoRef.current && !(await waitForFrames(videoRef.current, 2000))) {
          // Reused stream went stale (common on iOS after closing) — reacquire the camera.
          stopCameraStream();
          if (cancelled) return;
          s = await getCameraStream();
          attach(s);
        }
        const track = s.getVideoTracks()[0];
        trackRef.current = track;
        const caps = track.getCapabilities?.() || {};
        if (caps.torch) setTorch(false);
      } catch (e) {
        if (!cancelled) setError(e?.name === "NotAllowedError" ? "denied" : "unavailable");
      }
    })();
    return () => {
      cancelled = true;
      if (demo) demoStream?.getTracks().forEach((t) => t.stop());
      else releaseCameraStream();
    };
  }, []);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setBox({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const capture = useCallback(async (detectedQuad) => {
    const video = videoRef.current;
    if (!video || st.busy) return;
    st.busy = true;
    setFlash(true); setTimeout(() => setFlash(false), 350);
    setStatus("captured");
    if (navigator.vibrate) navigator.vibrate(40);
    const full = await grabFrame(video, trackRef.current);
    let page;
    if (optimiseRef.current) {
      // one optimised image per photo: crop → flatten → resize → JPEG; whole photo (resized) when the crop is doubtful
      const r = await optimisePhotoCanvas(full, optimiseOptsRef.current, detectedQuad);
      page = { photo: r.blob, meta: r.meta, cropped: r.cropped, thumb: thumbnail(r.canvas), full: URL.createObjectURL(r.blob) };
    } else {
      const original = await canvasToBlob(full, 0.95);
      // Auto-crop: re-detect the document on the captured frame (finer), fall back to the live quad.
      const cropQuad = detectOnCanvas(full) || detectedQuad;
      let processedCanvas = full;
      if (cropQuad) { try { processedCanvas = enhance(warpQuad(full, cropQuad)); } catch { processedCanvas = full; } }
      const processed = await canvasToBlob(processedCanvas, 0.92);
      page = { original, processed, thumb: thumbnail(processedCanvas), full: URL.createObjectURL(processed), cropped: !!cropQuad };
    }
    let next;
    setInvoices((prev) => {
      if (modeRef.current === "combine" && prev.length) next = [{ pages: [...prev[0].pages, page] }];
      else next = [...prev, { pages: [page] }];
      return next;
    });
    st.armed = false; st.lastQuad = detectedQuad; st.steady = 0; st.noDoc = 0;
    setTimeout(() => {
      st.busy = false;
      if (modeRef.current === "single") finishRef.current?.();
      else setStatus(autoRef.current ? "cooldown" : "manual");
    }, 600);
  }, [st]);

  useEffect(() => {
    wsRef.current = createWorkspace();
    focusRef.current = createWorkspace();
    let timer;
    const tick = () => {
      const video = videoRef.current;
      if (video && video.readyState >= 2 && !st.busy && !uploading && !landscapeRef.current) {
        syncDims();
        const r = analyzeFrame(video, wsRef.current);
        const good = r.found && r.fill > 0.55 && r.areaFrac < 0.95;
        setQuad(good ? r.quad : null);
        if (!good) {
          st.steady = 0; st.prevQuad = null; st.noDoc++; st.focus = [];
          if (!st.armed && st.noDoc >= REMOVED_FRAMES) st.armed = true;
          setStatus(!st.armed ? "cooldown" : autoRef.current ? "searching" : "manual");
        } else {
          st.noDoc = 0;
          if (!st.armed && st.lastQuad) {
            const [cx, cy] = quadCentroid(r.quad), [lx, ly] = quadCentroid(st.lastQuad);
            if (Math.hypot(cx - lx, cy - ly) > 0.25) st.armed = true;
          }
          const motion = quadMotion(st.prevQuad, r.quad);
          st.prevQuad = r.quad;
          if (!autoRef.current) setStatus(r.touchesBorder ? "partial" : "manual");
          else if (!st.armed) setStatus("cooldown");
          else if (r.touchesBorder) { st.steady = 0; setStatus("partial"); }
          else if (motion > MOTION_LIMIT) { st.steady = 0; st.focus = []; setStatus("detected"); }
          else {
            st.steady++;
            if (r.sharpness < SHARP_MIN) { setStatus("blurry"); st.steady = Math.min(st.steady, STEADY_FRAMES - 2); }
            else if (st.steady >= STEADY_FRAMES) {
              // Focus gate: high-res sharpness must be good and no longer improving (autofocus settled).
              const f = measureSharpness(video, r.bbox, focusRef.current);
              st.focus.push(f); if (st.focus.length > 6) st.focus.shift();
              const peak = Math.max(...st.focus);
              const settled = st.focus.length >= 2 && f >= peak * 0.85;
              const waited = st.steady >= STEADY_FRAMES + MAX_WAIT_FRAMES; // never hang: after ~1.2s steady, shoot the best we can (burst keeps the sharpest frame)
              if (f < FOCUS_MIN && !(waited && f >= FOCUS_MIN * 0.6)) { setStatus("blurry"); if (!waited) st.steady = STEADY_FRAMES - 1; }
              else if (settled || st.steady >= STEADY_FRAMES + 5 || waited) { st.focus = []; capture(r.quad); }
              else setStatus("steady");
            }
            else setStatus("steady");
          }
        }
      }
      timer = setTimeout(tick, ANALYSIS_MS);
    };
    tick();
    return () => clearTimeout(timer);
  }, [capture, st, uploading, syncDims]);

  const toggleAuto = () => { const v = !auto; setAuto(v); onAutoCaptureChange?.(v); st.steady = 0; setStatus(v ? "searching" : "manual"); };
  const toggleTorch = async () => { try { await trackRef.current.applyConstraints({ advanced: [{ torch: !torch }] }); setTorch(!torch); } catch { /* ignore */ } };
  const pickMode = (m) => { setMode(m); localStorage.setItem("supplivo_scan_mode", m); };
  const deletePage = (ii, pi) => setInvoices((prev) => prev.map((inv, i) => i === ii ? { pages: inv.pages.filter((p, j) => { if (j === pi && p.full) URL.revokeObjectURL(p.full); return j !== pi; }) } : inv).filter((inv) => inv.pages.length));
  const closeGallery = useCallback(() => setGallery(false), []);

  const finish = useCallback(async () => {
    if (!invoices.length) return onClose();
    setUploading(true); setUpProgress({ done: 0, total: mode === "combine" ? 1 : invoices.length });
    try { await onFinish(invoices, setUpProgress); } finally { setUploading(false); setUpProgress(null); }
  }, [invoices, onFinish, onClose]);
  finishRef.current = finish;
  useEffect(() => { if (mode === "single" && invoices.length === 1 && !st.busy && !uploading) finish(); }, [invoices, mode, finish, st, uploading]);

  const totalPages = invoices.reduce((a, i) => a + i.pages.length, 0);
  const lastThumb = invoices.length ? invoices[invoices.length - 1].pages.at(-1).thumb : null;
  const [statusText, statusTone] = STATUS[status] || STATUS.searching;
  const stroke = status === "steady" || status === "captured" ? "#10B981" : "#F59E0B";
  const ar = dims[0] / dims[1];
  const coverW = box.w && box.h ? Math.max(box.w, box.h * ar) : 0;
  const coverH = coverW / ar;

  return (
    <div className="fixed inset-0 z-50 bg-black text-white flex flex-col select-none" data-testid="document-scanner">
      {uploading && (
        <div className="absolute inset-0 z-[70] bg-black/85 flex flex-col items-center justify-center gap-4 text-center px-8" data-testid="upload-guard">
          <div className="rounded-2xl bg-rose-600 px-6 py-5 shadow-2xl max-w-sm w-full">
            <p className="font-display text-2xl font-bold text-white flex items-center justify-center gap-2"><AlertTriangle className="h-7 w-7" /> Don't close the app</p>
            <p className="mt-1 text-rose-100 text-sm">Your scans are still uploading. Closing or switching away now would lose the photos.</p>
            <div className="mt-4 h-2 w-full rounded-full bg-rose-900/60 overflow-hidden"><div className="h-full bg-white transition-[width] duration-500" style={{ width: `${upProgress?.total ? Math.round((upProgress.done / upProgress.total) * 100) : 5}%` }} /></div>
            <p className="mt-2 font-mono text-sm text-white" data-testid="upload-guard-progress">{upProgress ? `Uploading ${Math.min(upProgress.done + 1, upProgress.total)} of ${upProgress.total} invoice${upProgress.total === 1 ? "" : "s"}…` : "Uploading…"}</p>
          </div>
        </div>
      )}
      {landscape && (
        <div className="absolute inset-0 z-[60] bg-black/95 flex flex-col items-center justify-center gap-4 text-center px-8" data-testid="rotate-to-portrait">
          <Smartphone className="h-14 w-14 text-emerald-400 animate-[spin_3s_ease-in-out_infinite]" style={{ animationDirection: "alternate" }} />
          <p className="font-display text-xl font-bold">Turn your phone upright</p>
          <p className="text-sm text-slate-300">Invoices are scanned in portrait so the whole page fits and stays sharp.</p>
          <button onClick={onClose} className="mt-2 h-11 px-5 rounded-full bg-white/10 text-sm font-semibold" data-testid="rotate-close">Close scanner</button>
        </div>
      )}
      {flash && <div className="absolute inset-0 bg-white z-40 capture-flash pointer-events-none" />}
      {gallery && <Gallery invoices={invoices} onDelete={deletePage} onClose={closeGallery} />}

      <div className="relative z-20 flex items-center justify-between px-3 py-2 bg-black" style={{ paddingTop: "max(env(safe-area-inset-top), 8px)" }}>
        <div className="flex items-center gap-2">
          {torch !== null && <button onClick={toggleTorch} data-testid="torch-toggle" className={`h-12 w-12 grid place-items-center rounded-full ${torch ? "bg-amber-400 text-slate-950" : "bg-white/10"}`}>{torch ? <Flashlight /> : <FlashlightOff />}</button>}
          <button onClick={toggleAuto} data-testid="auto-capture-toggle" className={`h-12 px-4 rounded-full flex items-center gap-2 font-semibold text-sm ${auto ? "bg-emerald-500 text-slate-950" : "bg-white/10 text-white"}`}>
            {auto ? <Zap className="h-4 w-4" /> : <ZapOff className="h-4 w-4" />} Auto Capture: {auto ? "ON" : "OFF"}
          </button>
        </div>
        <button onClick={onClose} className="h-12 w-12 grid place-items-center rounded-full bg-white/10" data-testid="scanner-close-button" aria-label="Close scanner"><X /></button>
      </div>

      <div ref={boxRef} className="relative flex-1 min-h-0 overflow-hidden bg-black">
        {error ? (
          <div className="absolute inset-0 grid place-items-center p-6 text-center overflow-auto" data-testid="scanner-error">
            <div className="max-w-sm">
              <Camera className="mx-auto h-10 w-10 text-slate-500 mb-3" />
              {error === "denied" ? (
                <>
                  <p className="text-slate-100 font-semibold">Camera access is blocked</p>
                  <div className="text-sm text-slate-300 mt-2 text-left bg-white/5 rounded-lg p-3 space-y-1.5" data-testid="permission-help">
                    {IOS ? (<>
                      <p><b>iPhone / iPad:</b> Settings → Safari → Camera → <b>Allow</b>.</p>
                      <p>Or in Safari tap <b>AA</b> in the address bar → Website Settings → Camera → <b>Allow</b>.</p>
                    </>) : (<>
                      <p><b>Android / Chrome:</b> tap the <b>lock icon</b> in the address bar → Permissions → Camera → <b>Allow</b>.</p>
                      <p>Choose <b>“Allow”</b> rather than “Only this time”, so it isn't asked again.</p>
                    </>)}
                  </div>
                </>
              ) : <p className="text-slate-200">No camera could be opened on this device. You can still upload invoice photos or PDFs.</p>}
              <button onClick={onClose} className="mt-6 h-12 px-6 rounded-full bg-white text-slate-900 font-semibold">Back to upload</button>
            </div>
          </div>
        ) : (
          <>
            <div className="absolute" style={{ width: coverW || "100%", height: coverH || "100%", left: coverW ? (box.w - coverW) / 2 : 0, top: coverH ? (box.h - coverH) / 2 : 0 }} data-testid="scanner-viewport">
              <video ref={videoRef} playsInline muted onLoadedMetadata={syncDims} onResize={syncDims} className="block w-full h-full object-fill" data-testid="scanner-video" />
              <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                {quad && <polygon points={quad.map(([x, y]) => `${x * 100},${y * 100}`).join(" ")} fill={`${stroke}22`} stroke={stroke} strokeWidth="0.6" vectorEffect="non-scaling-stroke" style={{ transition: "all .12s linear" }} data-testid="document-outline" />}
              </svg>
            </div>
            <div className="absolute top-3 inset-x-0 flex justify-center px-4">
              <span className={`px-4 py-2 rounded-full text-sm font-semibold shadow-lg backdrop-blur ${statusTone} ${status === "steady" ? "scan-pulse" : ""}`} data-testid="scanner-status" data-status={uploading ? "uploading" : status}>{uploading ? "Uploading…" : statusText}</span>
            </div>
            <div className="absolute bottom-3 inset-x-0 flex justify-center px-4">
              <span className="text-xs text-white/90 bg-black/50 px-3 py-1 rounded-full" data-testid="scanner-hint">{MODES.find((m) => m[0] === mode)[2]}{totalPages ? ` · ${mode === "combine" ? `${totalPages} pages` : `Invoices captured: ${invoices.length}`}` : ""}</span>
            </div>
          </>
        )}
      </div>

      <div className="relative z-20 bg-black px-4 pt-2 pb-3 space-y-3" style={{ paddingBottom: "max(env(safe-area-inset-bottom), 12px)" }}>
        <div className="flex justify-center gap-6" data-testid="scan-mode-tabs">
          {MODES.map(([k, label]) => <button key={k} onClick={() => pickMode(k)} data-testid={`mode-${k}`} className={`h-10 text-sm font-semibold tracking-wide ${mode === k ? "text-amber-400" : "text-white/80"}`}>{label}</button>)}
        </div>
        <div className="flex items-center justify-between">
          <button onClick={() => invoices.length && setGallery(true)} className="w-16 h-16 rounded-lg overflow-hidden border-2 border-white/30 relative bg-white/5 grid place-items-center" data-testid="gallery-button" aria-label="View captures">
            {lastThumb ? <img src={lastThumb} alt="" className="w-full h-full object-cover" /> : <Images className="text-white/60" />}
            {totalPages > 0 && <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1 rounded-full bg-emerald-500 text-slate-950 text-xs font-bold grid place-items-center" data-testid="captured-counter">{mode === "combine" ? totalPages : invoices.length}</span>}
          </button>
          <button onClick={() => capture(quad)} disabled={!!error || uploading} data-testid="shutter-button" aria-label="Capture" className="h-[76px] w-[76px] rounded-full border-4 border-white grid place-items-center active:scale-95 transition-transform">
            <span className="h-[62px] w-[62px] rounded-full bg-white" />
          </button>
          <button onClick={finish} disabled={uploading} data-testid="finish-scanning-button" className={`h-12 px-4 rounded-full font-semibold flex items-center gap-2 min-w-[6.5rem] justify-center disabled:opacity-60 ${invoices.length ? "bg-emerald-500 text-slate-950" : "bg-white/10 text-white"}`}>
            <Check className="h-5 w-5" /> {uploading ? "…" : invoices.length ? `Finish (${mode === "combine" ? 1 : invoices.length})` : "Close"}
          </button>
        </div>
      </div>
    </div>
  );
}
